/**
 * ChartNode - Lexical decorator node for interactive charts.
 * Supports bar, line, area, and pie chart types.
 */

import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

import { CHART_TYPES, CHART_DEFAULT_PALETTE } from './chartShared';

const ChartComponent = React.lazy(() => import('./ChartComponent'));

function $convertChartElement(domNode) {
  const dataAttr = domNode.getAttribute('data-lexical-chart');
  if (!dataAttr) return null;
  try {
    const data = JSON.parse(dataAttr);
    const node = $createChartNode(data);
    return { node };
  } catch (e) {
    return null;
  }
}

export class ChartNode extends DecoratorNode {
  __chartType;
  __title;
  __showLegend;
  __width;
  __height;
  __categories;
  __series;

  static getType() {
    return 'chart';
  }

  static clone(node) {
    return new ChartNode(
      {
        chartType: node.__chartType,
        title: node.__title,
        showLegend: node.__showLegend,
        width: node.__width,
        height: node.__height,
        categories: node.__categories,
        series: node.__series,
      },
      node.__key,
    );
  }

  constructor(data, key) {
    super(key);
    const safe = data || {};
    this.__chartType = CHART_TYPES.includes(safe.chartType)
      ? safe.chartType
      : 'bar';
    this.__title = typeof safe.title === 'string' ? safe.title : '';
    this.__showLegend = safe.showLegend !== false;
    this.__width = Number(safe.width) || 480;
    this.__height = Number(safe.height) || 320;
    this.__categories = Array.isArray(safe.categories)
      ? safe.categories.map(String)
      : [];
    this.__series = Array.isArray(safe.series)
      ? safe.series.map((s, i) => ({
          name: typeof s?.name === 'string' ? s.name : `Series ${i + 1}`,
          color:
            typeof s?.color === 'string'
              ? s.color
              : CHART_DEFAULT_PALETTE[i % CHART_DEFAULT_PALETTE.length],
          colors: Array.isArray(s?.colors) ? s.colors.map(String) : undefined,
          data: Array.isArray(s?.data) ? s.data.map((v) => Number(v) || 0) : [],
        }))
      : [];
  }

  static importJSON(serializedNode) {
    return $createChartNode({
      chartType: serializedNode.chartType,
      title: serializedNode.title,
      showLegend: serializedNode.showLegend,
      width: serializedNode.width,
      height: serializedNode.height,
      categories: serializedNode.categories,
      series: serializedNode.series,
    });
  }

  exportJSON() {
    return {
      type: 'chart',
      version: 1,
      chartType: this.__chartType,
      title: this.__title,
      showLegend: this.__showLegend,
      width: this.__width,
      height: this.__height,
      categories: this.__categories,
      series: this.__series,
    };
  }

  static importDOM() {
    return {
      div: (domNode) => {
        if (!domNode.hasAttribute('data-lexical-chart')) return null;
        return {
          conversion: $convertChartElement,
          priority: 2,
        };
      },
    };
  }

  exportDOM() {
    const element = document.createElement('div');
    element.setAttribute(
      'data-lexical-chart',
      JSON.stringify(this.getChartData()),
    );
    element.style.display = 'inline-block';
    return { element };
  }

  createDOM(config) {
    const span = document.createElement('span');
    span.style.display = 'inline-block';
    if (config?.theme?.chart) {
      span.className = config.theme.chart;
    }
    return span;
  }

  updateDOM() {
    return false;
  }

  getChartData() {
    return {
      chartType: this.__chartType,
      title: this.__title,
      showLegend: this.__showLegend,
      width: this.__width,
      height: this.__height,
      categories: this.__categories,
      series: this.__series,
    };
  }

  setChartData(data) {
    const writable = this.getWritable();
    if (data.chartType && CHART_TYPES.includes(data.chartType)) {
      writable.__chartType = data.chartType;
    }
    if (typeof data.title === 'string') writable.__title = data.title;
    if (typeof data.showLegend === 'boolean') writable.__showLegend = data.showLegend;
    if (Number.isFinite(Number(data.width))) writable.__width = Number(data.width);
    if (Number.isFinite(Number(data.height))) writable.__height = Number(data.height);
    if (Array.isArray(data.categories)) {
      writable.__categories = data.categories.map(String);
    }
    if (Array.isArray(data.series)) {
      writable.__series = data.series.map((s, i) => ({
        name: typeof s?.name === 'string' ? s.name : `Series ${i + 1}`,
        color:
          typeof s?.color === 'string'
            ? s.color
            : CHART_DEFAULT_PALETTE[i % CHART_DEFAULT_PALETTE.length],
        colors: Array.isArray(s?.colors) ? s.colors.map(String) : undefined,
        data: Array.isArray(s?.data) ? s.data.map((v) => Number(v) || 0) : [],
      }));
    }
  }

  setSize(width, height) {
    const writable = this.getWritable();
    writable.__width = Math.max(Number(width) || 200, 200);
    writable.__height = Math.max(Number(height) || 160, 160);
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <ChartComponent
          nodeKey={this.getKey()}
          chartType={this.__chartType}
          title={this.__title}
          showLegend={this.__showLegend}
          width={this.__width}
          height={this.__height}
          categories={this.__categories}
          series={this.__series}
        />
      </Suspense>
    );
  }
}

export function $createChartNode(data) {
  return $applyNodeReplacement(new ChartNode(data));
}

// Re-export for backwards compatibility with existing imports.
export { CHART_TYPES, createDefaultChartData, $isChartNode } from './chartShared';

// Lexical registers node classes once; HMR must reload when ChartNode class changes.
if (process.env.NODE_ENV === 'development' && typeof module !== 'undefined' && module.hot) {
  const hotData = module.hot.data || (module.hot.data = {});
  if (hotData.ChartNodeKlass && hotData.ChartNodeKlass !== ChartNode) {
    window.location.reload();
  }
  hotData.ChartNodeKlass = ChartNode;

  module.hot.accept('./ChartComponent', () => {
    window.location.reload();
  });
  module.hot.accept('./chartShared', () => {
    window.location.reload();
  });
}
